import React, { useEffect, useRef } from "react";
import { useValue } from "tldraw";
import { useAgent } from "@/canvas/agent/TldrawAgentAppProvider";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { useAudioStore } from "@/features/tutor/store/useAudioStore";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import {
  useNotepadStore,
  selectIsAIActiveOnCurrentPage,
  selectIsNotepadBite,
  selectPreprocessComplete,
} from "../store/useNotepadStore";
import type { ChatHistoryPromptItem } from "@/shared/types/ChatHistoryItem";
import type { TldrawAgent } from "@/canvas/agent/TldrawAgent";
import type { AgentAction } from "@/shared/types/AgentAction";
import type { Streaming } from "@/shared/types/Streaming";
import type { TLShapeId } from "tldraw";
import { debugLog, debugWarn } from "@/utils/debugLog";
import { CANVAS_GATEWAY_CONFIGURED } from "@/flags";
import { WHITEBOARD_SCAN_EVENT } from "@/canvas/components/WhiteboardScan";

/** Minimum interval between voice context stream triggers (ms). */
const VOICE_DEBOUNCE_MS = 300;

/**
 * Fail-open timeout for the opening-turn preprocessing gate. The gateway
 * bounds its preprocess handoff await at 8s (PREPROCESS_HANDOFF_TIMEOUT_MS)
 * and always sends CANVAS_READY after — this gate must stay ABOVE that bound
 * plus delivery margin, or a late CANVAS_SEED gets buffered after the gate
 * fails open and is never applied while the prompt claims it was. If READY
 * never arrives at all (old gateway, gateway error), proceed anyway — a
 * missing seed degrades the lesson, a stuck gate bricks it.
 */
const PREPROCESS_GATE_TIMEOUT_MS = 10000;

/** True if any of the seed's shapes are already on the canvas (reconnect / restored state). */
function seedShapesExist(
  agent: TldrawAgent,
  actions: Record<string, unknown>[],
): boolean {
  for (const action of actions) {
    const shapeId = (action.shapeId ??
      (action.shape as Record<string, unknown> | undefined)?.shapeId) as
      string | undefined;
    if (shapeId && agent.editor.getShape(`shape:${shapeId}` as TLShapeId)) {
      return true;
    }
  }
  return false;
}

/**
 * Apply buffered CANVAS_SEED actions to the canvas, once. Skipped (but still
 * marked applied) only when the seed's own shapes are already on the board —
 * reseeding would duplicate them.
 *
 * Anything else on a restored board does not count. The gateway records the
 * seed as on the board on every connection, so a board without it — saved
 * blank by a fast exit, holding only the student's ink, or with the problem
 * erased — would have the agent talk about shapes that are not there.
 */
function applyPendingSeed(agent: TldrawAgent, owner: object | null): void {
  const store = useNotepadStore.getState();
  if (store.preprocessOwner !== owner || store.seedApplied) return;
  const actions = store.pendingSeedActions;
  if (!actions) return;

  if (seedShapesExist(agent, actions)) {
    debugLog("[CanvasVoiceBridge] Seed skipped (already on canvas)");
    store.markSeedApplied(owner);
    return;
  }

  agent.applySeedActions(actions as unknown as Streaming<AgentAction>[]);
  agent.editor.zoomToFit();
  store.markSeedApplied(owner);
  debugLog("[CanvasVoiceBridge] Seed applied:", actions.length, "actions");
}

/** Wait for readiness and seed application, or the fail-open timeout; abort releases the wait. */
function waitForPreprocess(
  agent: TldrawAgent,
  signal: AbortSignal,
  owner: object | null,
): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  if (!CANVAS_GATEWAY_CONFIGURED) {
    useNotepadStore.getState().markPreprocessGateSettled(owner);
    return Promise.resolve();
  }

  applyPendingSeed(agent, owner);
  if (selectPreprocessComplete(useNotepadStore.getState())) {
    useNotepadStore.getState().markPreprocessGateSettled(owner);
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      unsub();
      signal.removeEventListener("abort", finish);
      resolve();
    };
    const timer = setTimeout(() => {
      if (!signal.aborted) {
        debugWarn(
          "[CanvasVoiceBridge] Preprocess gate timed out — starting anyway",
        );
        useNotepadStore.getState().markPreprocessGateSettled(owner);
      }
      finish();
    }, PREPROCESS_GATE_TIMEOUT_MS);
    const unsub = useNotepadStore.subscribe((store) => {
      if (signal.aborted || store.preprocessOwner !== owner) {
        finish();
      } else if (selectPreprocessComplete(store)) {
        // Unsubscribe before notifying the store (Zustand notifies synchronously).
        finish();
        store.markPreprocessGateSettled(owner);
      }
    });
    signal.addEventListener("abort", finish, { once: true });
  });
}

/**
 * Canvas voice trigger bridge.
 *
 * Flow:
 * 1. TURN_CHANGE (source: "mic") fires at button press — bridge immediately starts
 *    a context-only canvas stream. The canvas won't change while the student is
 *    talking, so the context captured now is correct. Overlapping context delivery
 *    with the full press duration + STT eliminates the bridge wait.
 * 2. USER_AUDIO_DONE is kept as a safety fallback for edge cases where TURN_CHANGE
 *    did not trigger the stream (e.g. non-mic TURN_CHANGE or agent not ready).
 * 3. Canvas audio completion is sent over the gateway as CANVAS_AUDIO_DONE,
 *    and the backend combines that voice input with CANVAS_CONTEXT.
 *
 * On mount (canvas just opened and agent is ready), the bridge triggers an
 * opening greeting turn so the canvas agent starts the conversation immediately.
 */
interface CanvasVoiceBridgeProps {
  greetedPagesRef: React.MutableRefObject<Set<string>>;
  pageKey: string | null;
  onViewportChanged?: (hasChanged: boolean) => void;
}

export function CanvasVoiceBridge({
  greetedPagesRef,
  pageKey,
  onViewportChanged,
  canvasSessionId,
  isCurrentCanvas,
}: CanvasVoiceBridgeProps & {
  canvasSessionId: string | null;
  isCurrentCanvas: boolean;
}) {
  const ingressType = useNotepadStore((s) => s.ingressType);
  const sessionId = useSessionStore((s) => s.sessionId);

  // Session boot updates Zustand before the URL changes the canvas key.
  // Keep the old agent inert until the matching canvas has actually mounted.
  if (!isCurrentCanvas || !canvasSessionId || canvasSessionId !== sessionId) {
    return null;
  }

  // In student review mode, no voice bridge needed at all.
  if (ingressType === "student") return null;

  // For AI ingress, always mount the inner bridge. The inner component gates
  // its behavior on isAIActive so it's inert on non-latest pages.
  return (
    <CanvasVoiceBridgeInner
      greetedPagesRef={greetedPagesRef}
      pageKey={pageKey}
      onViewportChanged={onViewportChanged}
    />
  );
}

function CanvasVoiceBridgeInner({
  greetedPagesRef,
  pageKey,
  onViewportChanged,
}: CanvasVoiceBridgeProps) {
  const agent = useAgent();

  const hasViewportChanged = useValue(
    "canvasViewportChanged",
    () => agent?.requests.hasViewportChanged() ?? false,
    [agent],
  );
  useEffect(() => {
    onViewportChanged?.(hasViewportChanged);
  }, [hasViewportChanged, onViewportChanged]);
  const { subscribe } = useRuntimeContext();
  const isAIActive = useNotepadStore(selectIsAIActiveOnCurrentPage);
  const preprocessOwner = useNotepadStore((s) => s.preprocessOwner);

  // Required content must still arrive after the opening gate fails open.
  useEffect(() => {
    if (!agent || !isAIActive) return;
    const apply = () => applyPendingSeed(agent, preprocessOwner);
    const unsub = useNotepadStore.subscribe(apply);
    apply();
    return unsub;
  }, [agent, isAIActive, preprocessOwner]);
  // The canvas can be mounted before the lesson starts (see the greeting
  // effect below); this is how it learns the lesson actually began.
  const hasSession = useSessionStore((s) => s.sessionId !== null);
  /** Pages this mount has actually dispatched an opening turn for. */
  const greetingDispatchedRef = useRef<Set<string>>(new Set());
  const earlyStartTurnIdRef = useRef<string | null>(null);
  // Snapshot of "student changed the canvas" taken at talk-button press.
  // The early-started context stream builds a screenshot part which recomputes
  // agent.requests.hasViewportChanged() mid-turn (ScreenshotPartUtil), so by
  // USER_AUDIO_DONE the live flag is usually false — the scan gate must use
  // this press-time snapshot instead.
  const scanHadInkRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable ref so event handlers always see the latest value without re-subscribing
  const isAIActiveRef = useRef(isAIActive);
  isAIActiveRef.current = isAIActive;

  // Show "Listening..." cursor when user starts recording
  useEffect(() => {
    if (!agent) return;

    let prevRecording = useAudioStore.getState().isRecording;
    const unsub = useAudioStore.subscribe((state) => {
      if (state.isRecording !== prevRecording) {
        debugLog(
          "[CanvasVoiceBridge] isRecording changed:",
          prevRecording,
          "→",
          state.isRecording,
          "isAIActive:",
          isAIActiveRef.current,
        );
      }
      if (!isAIActiveRef.current) {
        prevRecording = state.isRecording;
        return;
      }
      if (state.isRecording && !prevRecording) {
        agent.cursor.showListening();
      }
      prevRecording = state.isRecording;
    });

    return unsub;
  }, [agent]);

  // Store user transcript from gateway STT in agent chat history
  useEffect(() => {
    if (!agent) return;

    const handler = (e: Event) => {
      if (!isAIActiveRef.current) return;
      const { transcript } = (e as CustomEvent).detail;
      if (!transcript) return;

      const item: ChatHistoryPromptItem = {
        type: "prompt",
        promptSource: "user",
        agentFacingMessage: transcript,
        userFacingMessage: transcript,
      };
      agent.chat.push(item);
      debugLog(
        "[CanvasVoiceBridge] Pushed user transcript to chat history:",
        transcript,
      );
      debugLog(
        "[CanvasVoiceBridge] Full chat history:",
        agent.chat.getHistory(),
      );
    };

    window.addEventListener("canvas:user-transcript", handler);
    return () => window.removeEventListener("canvas:user-transcript", handler);
  }, [agent]);

  // Work restored from a snapshot arrives as a programmatic store load, not a
  // user event, so hasViewportChanged stays false and the student returns to a
  // page holding their own work with the submit control inert. Seeded once per
  // page and only before the first turn: the work stays on the canvas forever
  // after, so re-arming on it would pin the button to "submit" for good.
  const inkSeededPagesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!agent || !isAIActive || !pageKey) return undefined;
    if (inkSeededPagesRef.current.has(pageKey)) return undefined;

    let disarmed = false;
    const disarm = () => {
      if (disarmed) return;
      disarmed = true;
      inkSeededPagesRef.current.add(pageKey);
      unlisten();
      window.removeEventListener("canvas:turn-start", disarm);
    };

    const seed = () => {
      if (disarmed) return;
      if (agent.requests.hasViewportChanged()) {
        disarm();
        return;
      }
      if (!agent.requests.hasStudentWorkOnPage()) return;
      agent.requests.setHasViewportChanged(true);
      debugLog("[CanvasVoiceBridge] Seeded pending work from restored work");
      disarm();
    };

    const unlisten = agent.editor.store.listen(seed, { scope: "document" });
    window.addEventListener("canvas:turn-start", disarm);
    seed();

    return () => {
      disarmed = true;
      unlisten();
      window.removeEventListener("canvas:turn-start", disarm);
    };
  }, [agent, isAIActive, pageKey]);

  // Trigger opening greeting on first visit, or resume prompt on revisit.
  // The greeting waits on the connection-level preprocessing gate: the gateway
  // signals CANVAS_READY once any pre-turn work (e.g. seeding the notepad bite
  // problem) is done, and the seed must be on the canvas before the opening
  // turn so its screenshot and shape context include it.
  useEffect(() => {
    if (!agent || !isAIActive || !pageKey) return;
    // Wait for the session before starting anything here. A notepad bite now
    // opens its canvas BEFORE the student presses Start Learning, so this
    // effect can run while there is no session — and `waitForPreprocess`
    // below starts a 10s gate that CANVAS_READY cannot possibly satisfy,
    // because the transport is deliberately holding its connect until a
    // session exists. Left ungated, a student who reads the modal for ten
    // seconds gets a failed-open gate that tears down the seed subscription
    // and marks the page greeted, and neither `agent` nor `isAIActive`
    // changes afterwards to run this again — blank paper and a silent tutor.
    //
    // The gate's budget was sized for the gateway's pre-turn work, not for
    // human reading time; starting the timer at Start Learning keeps it
    // measuring what it was chosen to measure.
    if (!hasSession) return;

    const isRevisit = greetedPagesRef.current.has(pageKey);
    let hasPersistedState = false;
    try {
      const raw = sessionStorage.getItem(`canvas_state_${pageKey}`);
      if (raw) {
        const saved = JSON.parse(raw);
        hasPersistedState =
          saved?.snapshot !== null && saved?.snapshot !== undefined;
      }
    } catch {
      // best-effort
    }
    if (isRevisit && !hasPersistedState) return;

    // At most ONE opening turn per page per mount, whatever the dep churn.
    //
    // `greetedPagesRef` cannot carry this on its own: on a revisit WITH
    // persisted state the effect is *supposed* to run again (that is the
    // "continue where you left off" prompt), so `isRevisit` short-circuits the
    // guard below rather than blocking. That was harmless while the effect
    // only re-ran on `agent`/`isAIActive`, but a notepad bite now mounts its
    // canvas before the lesson starts — so a snapshot exists early, and this
    // effect re-runs on `hasSession` and `pageKey` too. Each re-run then hit
    // the revisit path and fired another opening turn: the gateway logged four
    // `ws.turn_started` for the same greeting, each cancelling the last, and
    // the cancelled turns left op onsets unmatched (`ws.op_timings_missing`) —
    // which is what let `<op>` text reach TTS and the captions.
    if (greetingDispatchedRef.current.has(pageKey)) return;

    const controller = new AbortController();
    void (async () => {
      await waitForPreprocess(agent, controller.signal, preprocessOwner);
      if (
        controller.signal.aborted ||
        useNotepadStore.getState().preprocessOwner !== preprocessOwner
      )
        return;
      // Another effect run may have greeted this page while we waited.
      if (!isRevisit && greetedPagesRef.current.has(pageKey)) return;
      if (greetingDispatchedRef.current.has(pageKey)) return;

      const turnId = useTurnStore.getState().getTurnId();
      // Marked greeted only once the greeting can actually be sent. Marking
      // before this guard burned the page on a path that then bailed out, and
      // nothing re-runs this effect to undo that.
      if (!turnId) return;

      greetedPagesRef.current.add(pageKey);
      greetingDispatchedRef.current.add(pageKey);

      const userBounds = agent.editor.getViewportPageBounds();
      try {
        const currentMode = agent.mode.getCurrentModeType();
        if (currentMode === "working") {
          agent.interrupt();
        } else {
          agent.interrupt({ mode: "working" });
        }

        const isNotepadBite = selectIsNotepadBite(useNotepadStore.getState());
        let message: string;
        if (isRevisit) {
          message =
            "Continue teaching where you left off. Briefly acknowledge you're picking back up.";
        } else if (isNotepadBite) {
          message =
            "Start teaching. Use your opening phrase to greet the student.";
        } else {
          message = "Start teaching.";
        }

        void agent
          .request({
            source: "self",
            agentMessages: [message],
            userMessages: [],
            bounds: userBounds,
          })
          .catch((error) => {
            console.error(
              "[CanvasVoiceBridge] Canvas opened greeting request failed:",
              error,
            );
          });
      } catch (error) {
        console.error(
          "[CanvasVoiceBridge] Failed to trigger canvas opened greeting:",
          error,
        );
      }
    })();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, isAIActive, hasSession, pageKey, preprocessOwner]);

  useEffect(() => {
    if (!agent) return;

    const startCanvasVoiceContextStream = (turnId: string | null) => {
      if (!turnId || useTurnStore.getState().turnChangeFailed) return;
      // Don't start voice streams on non-latest pages
      if (!isAIActiveRef.current) return;

      const turnStore = useTurnStore.getState();
      if (turnStore.getTurnId() !== turnId) {
        turnStore.setTurnId(turnId);
      }

      const userBounds = agent.editor.getViewportPageBounds();
      try {
        // Cancel any in-flight canvas stream. Only force working mode if needed.
        const currentMode = agent.mode.getCurrentModeType();
        if (currentMode === "working") {
          agent.interrupt();
        } else {
          agent.interrupt({ mode: "working" });
        }

        // Issue a direct request so context dispatch is immediate.
        void agent
          .request({
            source: "self",
            agentMessages: [],
            userMessages: [],
            bounds: userBounds,
          })
          .catch((error) => {
            console.error(
              "[CanvasVoiceBridge] Canvas voice request failed:",
              error,
            );
          });
      } catch (error) {
        console.error(
          "[CanvasVoiceBridge] Failed to start canvas voice stream:",
          error,
        );
      }
    };

    // At button press, start the canvas stream with the current context. Using
    // queueMicrotask ensures the useWebsocketClient TURN_CHANGE handler (which sets
    // the new turn_id) has already run before we read it.
    const unsubTurnChange = subscribe(
      "TURN_CHANGE",
      (opts: { source?: string } | undefined) => {
        debugLog(
          "[CanvasVoiceBridge] TURN_CHANGE received, source:",
          opts?.source,
          "isAIActive:",
          isAIActiveRef.current,
        );
        if (opts?.source !== "mic") return;
        queueMicrotask(() => {
          if (useTurnStore.getState().turnChangeFailed) return;
          const turnId = useTurnStore.getState().getTurnId();
          debugLog(
            "[CanvasVoiceBridge] TURN_CHANGE mic microtask, turnId:",
            turnId,
          );
          if (!turnId) return;
          // Capture before startCanvasVoiceContextStream — request assembly
          // clears the flag via ScreenshotPartUtil.
          scanHadInkRef.current = agent.requests.hasViewportChanged();
          earlyStartTurnIdRef.current = turnId;
          setTimeout(() => {
            startCanvasVoiceContextStream(turnId);
          }, 0);
        });
      },
    );

    // Safety fallback: if TURN_CHANGE didn't start the stream for this turn
    // (e.g. agent wasn't ready), start it now.
    const unsubAudioDone = subscribe("USER_AUDIO_DONE", () => {
      const turnId = useTurnStore.getState().getTurnId();
      debugLog(
        "[CanvasVoiceBridge] USER_AUDIO_DONE received, turnId:",
        turnId,
        "isAIActive:",
        isAIActiveRef.current,
        "earlyStartTurnId:",
        earlyStartTurnIdRef.current,
      );
      if (!turnId || useTurnStore.getState().turnChangeFailed) return;

      // User released S — switch cursor from "listening" to "thinking".
      if (isAIActiveRef.current) {
        // Fire the green "scanning your work" sweep at submit time — but only
        // when the student actually changed the canvas since the last agent
        // turn (same signal that drives the Tap-to-Submit button). A voice-only
        // question shouldn't re-scan unchanged work.
        //
        // Read whichever signal the gateway is going to see, which differs by
        // path. TURN_CHANGE captured the flag at press (scanHadInkRef), so use
        // that — reading live there would sweep for ink drawn while the button
        // was held, which the gateway, deciding on the press-time value, would
        // then refuse. The fallback path builds its context below, after
        // release, so its live value is the one that ships.
        const usedEarlyStart = earlyStartTurnIdRef.current === turnId;
        const hadInk = usedEarlyStart
          ? scanHadInkRef.current
          : agent.requests.hasViewportChanged();
        scanHadInkRef.current = false;
        if (hadInk) {
          window.dispatchEvent(new CustomEvent(WHITEBOARD_SCAN_EVENT));
        }
        try {
          agent.cursor.showThinking();
          debugLog("[CanvasVoiceBridge] cursor → thinking");
        } catch {
          // Agent/editor may already be disposed if canvas is closing.
          debugLog(
            "[CanvasVoiceBridge] cursor.showThinking() failed (disposed?)",
          );
        }
      } else {
        debugLog(
          "[CanvasVoiceBridge] skipping showThinking — isAIActive is false",
        );
      }

      // TURN_CHANGE already started the stream for this turn — skip.
      if (earlyStartTurnIdRef.current === turnId) return;

      // Fallback: debounce rapid USER_AUDIO_DONE fires (e.g. fast taps).
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        const latestTurnId = useTurnStore.getState().getTurnId();
        if (latestTurnId && earlyStartTurnIdRef.current !== latestTurnId) {
          startCanvasVoiceContextStream(latestTurnId);
        }
      }, VOICE_DEBOUNCE_MS);
    });

    return () => {
      unsubTurnChange();
      unsubAudioDone();
      // Cancel any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Cancel any in-flight canvas stream on unmount (Phase 1.7)
      try {
        agent.cancel();
      } catch {
        // Agent may already be disposed
      }
    };
  }, [agent, subscribe]);

  return null;
}
